import React from 'react';
import styled from 'styled-components';
import type { LocalParticipant } from 'twilio-video';

import { isStreamingEnabled } from 'helpers/features';
import { AvatarSize } from 'components/Avatar';
import IconGlitter from 'components/Icons/IconGlitter';
import { useVideoContext } from 'components/VideoChat/VideoContext';

import { useChatWindowContext } from '../ChatWindow/ChatWindowContext';
import AudioTrack from '../Track/AudioTrack';
import VideoTrack from '../Track/VideoTrack';
import { getParticipantTracks } from './helpers';
import { CameraDisabledIndicator } from './Indicators/CameraDisabledIndicator';
import { MicrophoneDisabledIndicator } from './Indicators/MicrophoneDisabledIndicator';
import { NetworkQuality } from './Indicators/NetworkQuality';
import { ParticipantWrapper } from './ParticipantWrapper';
import { BottomStatusBar, TopStatusBar } from './StatusBar';

const TranscriptionIndicator = styled(IconGlitter)`
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 99;
  height: 28px;
  width: 28px;
  color: white;
  transform: scale(0.6);
  animation: blinker 2s linear infinite;

  @keyframes blinker {
    50% {
      opacity: 0.5;
    }
  }
`;
interface Props {
  participant: LocalParticipant;
}

export function LocalParticipantComponent({ participant }: Props) {
  const { videoTrack, audioTrack } = getParticipantTracks(participant);
  const { isTranscriptionEnabled } = useVideoContext();
  const { isUISmall } = useChatWindowContext();

  return (
    <ParticipantWrapper>
      <TopStatusBar>
        <NetworkQuality participant={participant} isLocal />
      </TopStatusBar>

      {isTranscriptionEnabled && isStreamingEnabled() && <TranscriptionIndicator />}

      <BottomStatusBar>
        <CameraDisabledIndicator videoTrack={videoTrack} />
        <MicrophoneDisabledIndicator audioTrack={audioTrack} />
      </BottomStatusBar>

      <VideoTrack
        participantIdentity={participant.identity}
        track={videoTrack?.track}
        avatarSize={isUISmall ? AvatarSize.S : AvatarSize.M}
      />
      <AudioTrack track={audioTrack?.track} id="local-participant-audio" />
    </ParticipantWrapper>
  );
}
